/**
 * PolicyRule entity validation rules (save-blocking and constraints).
 */

import { getFormValue, getDisplayNameFromForm } from './ReusableValidation';
import type { ValidationResult } from './ReusableValidation';

const ENTITLEMENT_KEYS = [
    'ReadEntity', '$.ReadEntity',
    'CreateEntity', '$.CreateEntity',
    'ModifyEntity', '$.ModifyEntity',
    'DeleteEntity', '$.DeleteEntity',
    'AddValue', '$.AddValue',
    'SetValue', '$.SetValue',
    'RemoveValue', '$.RemoveValue',
    'DeleteValue', '$.DeleteValue',
    'Decrypt', '$.Decrypt',
];

function hasAnyEntitlement(formData: Record<string, any>): boolean {
    for (let i = 0; i < ENTITLEMENT_KEYS.length; i += 2) {
        const val = getFormValue(formData, [ENTITLEMENT_KEYS[i], ENTITLEMENT_KEYS[i + 1]]);
        if (val === true || val === 'true' || val === 'True') return true;
    }
    return false;
}

/**
 * Validate PolicyRule-specific constraints (required fields and business rules).
 * Used by formValidationUtils.validateFormData when isPolicyRule is true.
 */
export function validatePolicyRule(
    formData: Record<string, any>,
    relativeTargetAttributeError: string | null
): ValidationResult {
    if (!getDisplayNameFromForm(formData)) {
        return { isValid: false, error: 'Display Name is required' };
    }

    const targetSet = getFormValue(formData, ['TargetSet', '$.TargetSet']);
    if (!targetSet) {
        return { isValid: false, error: 'Target Set is required to determine entity type' };
    }

    const relativeTarget = getFormValue(formData, ['RelativeTargetAttribute', '$.RelativeTargetAttribute']);
    const requestorSet = getFormValue(formData, ['RequestorSet', '$.RequestorSet']);
    if (relativeTarget && requestorSet) {
        return {
            isValid: false,
            error: 'Cannot have both Target Relative Attribute and Requestor Set. Please clear one of them before saving.'
        };
    }

    if (!relativeTarget && !requestorSet) {
        return {
            isValid: false,
            error: 'Either a Requestor Set or a Relative Target Attribute is required.'
        };
    }

    if (relativeTargetAttributeError) {
        return { isValid: false, error: relativeTargetAttributeError };
    }

    if (!hasAnyEntitlement(formData)) {
        return {
            isValid: false,
            error: 'At least one permission must be enabled (e.g. Read, Create, Modify, Delete).'
        };
    }

    const allAttributes = getFormValue(formData, ['AllAttributes', '$.AllAttributes']);
    if (allAttributes === false || allAttributes === 'false' || allAttributes === 'False') {
        const attrs = getFormValue(formData, ['Attributes', '$.Attributes']);
        if (!attrs || (Array.isArray(attrs) && attrs.length === 0)) {
            return {
                isValid: false,
                error: 'Select at least one attribute, or enable "Apply to All Attributes".'
            };
        }
    }

    return { isValid: true, error: null };
}
